// 2) Third-party libraries
import { all, call, delay, fork, put, select, takeLatest } from "redux-saga/effects";
import { fetchAuthSession } from "@aws-amplify/auth";
import { get } from "lodash-es";

// 3) Local imports
// 3.1 Constants
import { REPORTS_PAGE } from "../../config/tableAndPageConstants";
import { maxAPIRefetchCount, refetchAPIDelay } from "../../config/config";

// 3.3 Project-specific modules � components/types/other
import client from "src/api";
import { _queries as queries } from "../../graphql/queries.graphql";

// 3.4 Utility functions / types
import {
    checkShouldRequestRefetch,
    getGraphqlQueryString,
    getRegionallyApiResponse,
    getRegionConfigFromList,
    getSortFieldsWithCustomFields,
    isDocumentNode,
    removeAppliedFiltersForApiRequest,
} from "../../utils/commonFunctions";
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { ApplicationState } from "../storeTypes";
import { getRegionKeyConfig, getRegionSettingsConfig } from "../auth/authSlice";
import { getOrganisationActionUrl } from "../organisations/organisationSlice";
import {
    getScheduledReportDataErrorAction,
    getScheduledReportDataSuccessAction,
    getScheduledReportsErrorAction,
    getScheduledReportsRequestAction,
    getScheduledReportsSuccessAction,
    deleteScheuledReportsAction,
    getScheduledReportsForOrganisationRequestAction,
    getScheduledReportDataRequestAction,
    getScheduledReportDataForOrganisationRequestAction,
    createScheduledReportRequest,
} from "./scheduledReportSlice";

/**
 * Function called when creating scheduled report request.
 * @param param0
 */
function* handleCreateScheduledReportRequest({ payload: sagaPayload }: any) {
    const { callback, ...fields } = sagaPayload;
    
    const payload = {
        ...fields,
    };

    payload.Query = isDocumentNode(payload.Query)
        ? getGraphqlQueryString(payload.Query)
        : payload.Query;

    try {
        let apiUrl: string | undefined = get(payload, 'ApiUrl');
        
        if (!apiUrl) {
            apiUrl = yield select(
                getOrganisationActionUrl
            );
        }
        
        yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/report/schedule',
            'POST',
            payload
        );

        if (callback) {
            const response = {
                IsSuccess: true
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
        
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleGetScheduledReportsRequest({ payload }: any) {
    const errorMessage = 'Error fetching report list. Please try again later.';
    let { refetchCount } = get(payload, 'refetchCount') || 0;
    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            excludeReports,
            Region,
            CustomerLabel
        } = payload;
     
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'report',
            undefined,
            CustomerLabel
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_SCHEDULED_REPORT_FOR_COMPANY, 
                variables: {
                    ...cleanFilters,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * REPORTS_PAGE.pageSize,
                    ExcludeReports: excludeReports
                }
            }
        );

        const { ReportSchedules } = get(res.data, 'GetReportSchedulesForCompany');
        if (ReportSchedules) {
            const responsePayload = {
                data: ReportSchedules,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ReportSchedules.length < pageSize) &&
                        !(pageSize < REPORTS_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getScheduledReportsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getScheduledReportsSuccessAction({ ...payload, refetchCount }));
        } else {
            yield put(getScheduledReportsErrorAction([errorMessage]));
        }
    }
}

function* handleGetScheduledReportsForOrganisationRequest({ payload }: any) {
    const errorMessage = 'Error fetching report list. Please try again later.';
    let { refetchCount } = get(payload, 'refetchCount') || 0;
    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            excludeReports,
            Region,
            OrganisationId
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'report'
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        let apiUrl: string | undefined = undefined;
        if (Region) {
            let regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            let regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                Region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;
        const usedQuery = queries.GET_SCHEDULED_REPORT_FOR_ORGANISATION;
        const variables = {
            ...cleanFilters,
            ...sortObject,
            Ascending: sortAscending,
            PageSize: pageSize,
            Skip: currentPage * REPORTS_PAGE.pageSize,
            ExcludeReports: excludeReports,
            OrganisationId: OrganisationId
        };

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken.toString();
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(usedQuery),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, 'graphql'],
                {
                    query: usedQuery, 
                    variables
                }
            );
        }

        const { ReportSchedules } = get(res.data, 'GetReportSchedulesForOrganisation') || {};
        if (ReportSchedules) {
            const responsePayload = {
                data: ReportSchedules,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ReportSchedules.length < pageSize) &&
                        !(pageSize < REPORTS_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getScheduledReportsSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getScheduledReportsRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getScheduledReportsErrorAction([errorMessage]));
        }
    }
}

function* handleGetScheduledReportDataRequest({ payload }: any) {
    const errorMessage = 'Error fetching report details. Please try again later.';
    
    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, 'graphql'],
            {
                query: queries.GET_SCHEDULED_REPORT_DETAILS, 
                variables: {
                    ScheduleId: payload,
                }
            }
        );

        const Report = get(res.data, 'GetReportScheduleDetails');
        if (Report) {
            const responsePayload = {
                record: Report,
            };
            yield put(getScheduledReportDataSuccessAction(responsePayload));
        } else {
            yield put(getScheduledReportDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getScheduledReportDataErrorAction([errorMessage]));
    }
}

function* handleGetScheduledReportDataForOrganisationRequest({ payload, type }: any) {
    const errorMessage = 'Error fetching report details. Please try again later.';
    const { ScheduleId, Region } = payload;

    try {
        let apiUrl: string | undefined = undefined;
        if (Region) {
            let regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            let regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );
            const configUsed = getRegionConfigFromList(
                Region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;
        const usedQuery = queries.GET_SCHEDULED_REPORT_DETAILS_FOR_ORGANISATION;
        const variables = {
            ScheduleId
        };

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken.toString();
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(usedQuery),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, 'graphql'],
                {
                    query: usedQuery, 
                    variables
                }
            );
        }

        const Report = get(res.data, 'GetReportScheduleDetailsForOrganisation');
        if (Report) {
            const responsePayload = {
                record: Report,
            };
            yield put(getScheduledReportDataSuccessAction(responsePayload));
        } else {
            yield put(getScheduledReportDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getScheduledReportDataErrorAction([errorMessage]));
    }
}

function* handleDeleteScheduledReportsRequest({ payload: sagaPayload }: any) {
    const { key, CompanyId, callback } = sagaPayload;

    const payload = {
        ScheduleIds: key,
        CompanyId: CompanyId
    };

    try {
        const apiUrl: string | undefined = yield select(getOrganisationActionUrl);
        const apiResponse: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/report/schedule',
            'DELETE',
            payload
        );
        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: get(apiResponse, 'Messages')
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;
        
            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }
            returnData.IsSuccess = false;
            callback(returnData);
        }
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

function* watchCreateScheduledReportRequest() {
    yield takeLatest(
        createScheduledReportRequest.type,
        handleCreateScheduledReportRequest
    );
}

function* watchGetScheduledReportDataForOrganisationRequest() {
    yield takeLatest(
        getScheduledReportDataForOrganisationRequestAction.type,
        handleGetScheduledReportDataForOrganisationRequest
    );
}

function* watchGetScheduledReportDataRequest() {
    yield takeLatest(
        getScheduledReportDataRequestAction.type,
        handleGetScheduledReportDataRequest
    );
}

function* watchGetScheduledReportsRequest() {
    yield takeLatest(
        getScheduledReportsRequestAction.type,
        handleGetScheduledReportsRequest
    );
}

function* watchGetScheduledReportsForOrganisationRequest() {
    yield takeLatest(
        getScheduledReportsForOrganisationRequestAction.type,
        handleGetScheduledReportsForOrganisationRequest
    );
}

function* watchDeleteScheduledReportRequest() {
    yield takeLatest(
        deleteScheuledReportsAction.type,
        handleDeleteScheduledReportsRequest
    );
}

function* scheduledReportSaga() {
    yield all([
        fork(watchCreateScheduledReportRequest),
        fork(watchGetScheduledReportsRequest),
        fork(watchGetScheduledReportDataRequest),
        fork(watchGetScheduledReportDataForOrganisationRequest),
        fork(watchGetScheduledReportsForOrganisationRequest),
        fork(watchDeleteScheduledReportRequest),
    ]);
}

export default scheduledReportSaga;
